import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useContextStateManagement } from "../../context/ContextApi";
import { fetchRecordingPlaybackUrl } from "../../apiFunctions/recordingMediaApi";
import { usePracticeTimezone } from "../../hooks/usePracticeTimezone";
import "./VoiceOpsPage.css";

const isVoiceOpsEnabled = process.env.REACT_APP_VOICE_OPS_ENABLED !== "false";

function formatDuration(seconds) {
  if (!Number.isFinite(seconds)) return "0s";
  if (seconds >= 3600) {
    return `${(seconds / 3600).toFixed(1)}h`;
  }
  if (seconds >= 60) {
    return `${Math.ceil(seconds / 60)}m`;
  }
  return `${Math.round(seconds)}s`;
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return "0%";
  return `${Math.round(value * 100)}%`;
}

function formatPercentOrNA(value, available = true) {
  if (!available) return "N/A";
  return formatPercent(value);
}

function formatCompact(value) {
  if (!Number.isFinite(Number(value))) return "0";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value));
}

function formatDateTime(value, timeZone) {
  if (!value) return "Pending";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Pending";
  return timeZone ? date.toLocaleString("en-US", { timeZone }) : date.toLocaleString();
}

function formatLatency(value) {
  if (!Number.isFinite(Number(value))) return "Unavailable";
  return `${Math.round(Number(value))} ms`;
}

function normalizeSid(value) {
  return String(value || "").trim();
}

function findRecordingForSession(session, recording, recordings) {
  const targetRecordingSid = normalizeSid(recording?.sid);
  const targetCallSid = normalizeSid(session?.callSid);
  const targetRootCallSid = normalizeSid(session?.rootCallSid);

  return (recordings || []).find((item) => {
    const recordingSid = normalizeSid(item?.sid);
    const recordingCallSid = normalizeSid(item?.callSid);
    const recordingRootCallSid = normalizeSid(item?.rootCallSid);
    const relatedCallSids = Array.isArray(item?.relatedCallSids)
      ? item.relatedCallSids.map((sid) => normalizeSid(sid)).filter(Boolean)
      : [];

    return (
      (targetRecordingSid && recordingSid === targetRecordingSid) ||
      (targetCallSid && recordingCallSid === targetCallSid) ||
      (targetCallSid && recordingRootCallSid === targetCallSid) ||
      (targetCallSid && relatedCallSids.includes(targetCallSid)) ||
      (targetRootCallSid && recordingCallSid === targetRootCallSid) ||
      (targetRootCallSid && recordingRootCallSid === targetRootCallSid) ||
      (targetRootCallSid && relatedCallSids.includes(targetRootCallSid))
    );
  });
}

function parseFilters(searchParams, sessionId) {
  return {
    mode: sessionId ? "dev" : searchParams.get("mode") === "dev" ? "dev" : "exec",
    section: searchParams.get("section") === "outbound" ? "outbound" : "all",
    practiceId: searchParams.get("practiceId") || "all",
    range: searchParams.get("range") || "24h",
    status: searchParams.get("status") || "",
    direction: searchParams.get("direction") || "",
    transferState: searchParams.get("transferState") || "",
    voicemail: searchParams.get("voicemail") || "",
    provider: searchParams.get("provider") || "",
    model: searchParams.get("model") || "",
    search: searchParams.get("search") || "",
  };
}

function buildApiParams(filters) {
  const params = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (key === "mode" || key === "section") return;
    if (value !== null && value !== undefined && value !== "" && value !== "all") {
      params[key] = value;
    }
  });
  if (!params.range) {
    params.range = "24h";
  }
  return params;
}

function applySectionDirection(params, filters) {
  if (filters.section === "outbound") {
    return { ...params, direction: "outbound" };
  }
  return params;
}

function buildParamsString(baseFilters, overrideMode) {
  const next = new URLSearchParams();
  const params = { ...baseFilters, mode: overrideMode || baseFilters.mode };
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      next.set(key, value);
    }
  });
  return next.toString();
}

function countAppliedFilters(filters) {
  let count = 0;
  if (filters.range && filters.range !== "24h") count += 1;
  if (filters.status) count += 1;
  if (filters.section !== "outbound" && filters.direction) count += 1;
  if (filters.transferState) count += 1;
  return count;
}

function getFilterSummary(filters) {
  const parts = [];
  if (filters.range) {
    parts.push(filters.range);
  }
  if (filters.status) {
    parts.push(`Status: ${filters.status}`);
  }
  if (filters.section !== "outbound" && filters.direction) {
    parts.push(`Direction: ${filters.direction}`);
  }
  if (filters.transferState) {
    parts.push(`Transfer: ${filters.transferState}`);
  }
  return parts.join(" | ") || "Range, status, direction, transfer";
}

function getHealthTone(status) {
  if (status === "healthy") return "healthy";
  if (status === "degraded") return "warning";
  if (status === "critical") return "critical";
  return "muted";
}

function renderStatusPill(label, tone) {
  return <span className={`voice-ops-pill ${tone}`}>{label}</span>;
}

// Short, human-scannable fingerprint for a release digest ("sha256:<64 hex chars>") - the full
// digest is still available via the badge's title tooltip for anyone who needs to match it
// exactly against an AgentReleases row.
export function shortDigest(digest) {
  const value = String(digest || "").trim();
  if (!value) return "";
  const withoutPrefix = value.startsWith("sha256:") ? value.slice(7) : value;
  return withoutPrefix.slice(0, 7);
}

export function formatConfigSourceLabel(source) {
  const normalized = String(source || "").trim();
  if (!normalized) return "";
  return normalized
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

// "release" (the normal case - this call ran the practice's actual live release) stays muted/
// unobtrusive. Anything else (legacy, stale-cache, unavailable, ...) signals the worker couldn't
// resolve config the normal way, so it gets a visually distinct tone - critical specifically for
// "unavailable" (config couldn't be resolved at all), warning for the rest.
export function releaseBadgeTone(configSource) {
  const normalized = String(configSource || "").toLowerCase();
  if (!normalized || normalized === "release") return "muted";
  if (normalized === "unavailable") return "critical";
  return "warning";
}

// Release traceability badge (CONTRACT.md section 10) for a call/session row - built from
// whatever subset of releaseDigest/configSource/workerBuildSha that row actually carries. Returns
// null (renders nothing) when a row has none of the three, e.g. a call from before this
// traceability existed.
function renderReleaseBadge(releaseDigest, configSource, workerBuildSha) {
  if (!releaseDigest && !configSource && !workerBuildSha) return null;

  const normalizedSource = String(configSource || "").toLowerCase();
  const isNonRelease = normalizedSource && normalizedSource !== "release";
  const digestPart = shortDigest(releaseDigest);
  const label = isNonRelease
    ? formatConfigSourceLabel(configSource)
    : digestPart
      ? `Rel ${digestPart}`
      : "Release";

  const title = [
    releaseDigest ? `Release digest: ${releaseDigest}` : null,
    configSource ? `Config source: ${formatConfigSourceLabel(configSource)}` : null,
    workerBuildSha ? `Worker build: ${workerBuildSha}` : null,
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <span
      className={`voice-ops-pill ${releaseBadgeTone(configSource)}`}
      title={title || undefined}
    >
      {label}
    </span>
  );
}

function formatProviderLabel(source) {
  const normalized = String(source || "").toLowerCase();
  if (normalized === "livekit") return "LiveKit";
  if (normalized === "twilio") return "Twilio";
  if (normalized === "bedrock") return "Bedrock";
  if (normalized === "tts") return "TTS";
  return source;
}

function healthRank(status) {
  if (status === "critical") return 3;
  if (status === "degraded") return 2;
  if (status === "healthy") return 1;
  return 0;
}

function mergeProviderHealth(providers) {
  const grouped = new Map();
  const sourceToGroup = {
    deepgram: "tts",
    elevenlabs: "tts",
  };

  (providers || []).forEach((provider) => {
    const source = String(provider?.source || "").toLowerCase();
    if (!source || source === "console") {
      return;
    }

    const groupKey = sourceToGroup[source] || source;
    const current = grouped.get(groupKey) || {
      source: groupKey,
      status: "healthy",
      totalEvents: 0,
      errorEvents: 0,
      errorRate: 0,
      errorRateAvailable: true,
      lastSeenAt: null,
      avgConversationLatencyMs: null,
      metricLabel: null,
      _latencyTotal: 0,
      _latencyCount: 0,
    };

    current.totalEvents += Number(provider.totalEvents || 0);
    current.errorEvents += Number(provider.errorEvents || 0);
    if (healthRank(provider.status) > healthRank(current.status)) {
      current.status = provider.status;
    }
    if (provider.metricLabel) {
      current.metricLabel = provider.metricLabel;
    }
    if (provider.errorRateAvailable === false) {
      current.errorRateAvailable = false;
    }

    if (provider.lastSeenAt) {
      const currentLastSeen = current.lastSeenAt ? new Date(current.lastSeenAt) : null;
      const nextLastSeen = new Date(provider.lastSeenAt);
      if (!currentLastSeen || nextLastSeen > currentLastSeen) {
        current.lastSeenAt = provider.lastSeenAt;
      }
    }

    if (
      provider.avgConversationLatencyMs !== null &&
      provider.avgConversationLatencyMs !== undefined &&
      Number.isFinite(Number(provider.avgConversationLatencyMs))
    ) {
      current._latencyTotal += Number(provider.avgConversationLatencyMs);
      current._latencyCount += 1;
    }

    grouped.set(groupKey, current);
  });

  return ["livekit", "twilio", "tts", "bedrock"]
    .map((key) => grouped.get(key))
    .filter(Boolean)
    .map((provider) => {
      const errorRate =
        provider.totalEvents > 0 ? provider.errorEvents / provider.totalEvents : 0;
      let status = provider.status || "healthy";
      if (provider.source !== "twilio") {
        status = "healthy";
        if (errorRate >= 0.25) {
          status = "critical";
        } else if (errorRate >= 0.1) {
          status = "degraded";
        }
      }

      return {
        ...provider,
        status,
        errorRate:
          provider.errorRateAvailable === false ? null : errorRate,
        avgConversationLatencyMs:
          provider._latencyCount > 0
            ? provider._latencyTotal / provider._latencyCount
            : null,
      };
    });
}

export function VoiceOpsPage() {
  const { userType, practicesList, setSideBarLinkActive, token } =
    useContextStateManagement();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => parseFilters(searchParams, sessionId),
    [searchParams, sessionId],
  );
  // "all" means this view spans every practice at once (each row can belong to a different
  // one) - resolving a single timezone wouldn't be meaningful there, so this only resolves
  // when a specific practice is selected, falling back to DEFAULT_PRACTICE_TIMEZONE otherwise.
  const practiceTimezone = usePracticeTimezone(
    filters.practiceId !== "all" ? filters.practiceId : null,
    token,
  );

  const [overview, setOverview] = useState(null);
  const [sessionsPayload, setSessionsPayload] = useState({ rows: [], total: 0 });
  const [sessionDetail, setSessionDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [streamStatus, setStreamStatus] = useState("connecting");
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isMoreDetailsOpen, setIsMoreDetailsOpen] = useState(false);
  const [isTwilioCodesOpen, setIsTwilioCodesOpen] = useState(false);
  const [recordingMediaUrl, setRecordingMediaUrl] = useState("");
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState("");

  const refreshTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pollingTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const filterMenuRef = useRef(null);

  const adminPractices = Array.isArray(practicesList)
    ? practicesList.filter((practice) => practice?.UserName !== "Admin")
    : [];
  const providerHealthCards = useMemo(
    () => mergeProviderHealth(overview?.providerHealth),
    [overview?.providerHealth],
  );
  const twilioObservedCodes = Array.isArray(overview?.twilioHealth?.observedCodes)
    ? overview.twilioHealth.observedCodes
    : [];

  useEffect(() => {
    setSideBarLinkActive("voiceOps");
  }, [setSideBarLinkActive]);

  useEffect(() => {
    if (!sessionId) return;
    if (searchParams.get("mode") === "dev") return;
    const next = new URLSearchParams(searchParams.toString());
    next.set("mode", "dev");
    setSearchParams(next, { replace: true });
  }, [searchParams, sessionId, setSearchParams]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      if (pollingTimerRef.current) window.clearInterval(pollingTimerRef.current);
      setRecordingMediaUrl((currentUrl) => {
        if (currentUrl && currentUrl.startsWith("blob:")) {
          URL.revokeObjectURL(currentUrl);
        }
        return "";
      });
    };
  }, []);

  useEffect(() => {
    setIsMoreDetailsOpen(false);
    setIsTwilioCodesOpen(false);
  }, [sessionId]);

  useEffect(() => {
    function handleDocumentClick(event) {
      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target)
      ) {
        setIsFilterMenuOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        setIsFilterMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!isMoreDetailsOpen && !isTwilioCodesOpen) return undefined;

    function handleEscape(event) {
      if (event.key === "Escape") {
        setIsMoreDetailsOpen(false);
        setIsTwilioCodesOpen(false);
      }
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMoreDetailsOpen, isTwilioCodesOpen]);

  function updateFilters(updates) {
    const nextFilters = {
      ...filters,
      ...updates,
    };
    const next = new URLSearchParams();
    Object.entries(nextFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        next.set(key, value);
      }
    });
    setSearchParams(next, { replace: false });
  }

  function openSession(targetSessionId) {
    navigate(`/VoiceOps/${targetSessionId}?${buildParamsString(filters, "dev")}`);
  }

  function closeSession() {
    navigate(`/VoiceOps?${buildParamsString(filters, "dev")}`);
  }

  function scheduleRefresh() {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = window.setTimeout(() => {
      setRefreshNonce((value) => value + 1);
    }, 800);
  }

  useEffect(() => {
    if (!token || !isVoiceOpsEnabled) return undefined;

    const controller = new AbortController();
    let disposed = false;
    const outboundMissingPractice =
      filters.section === "outbound" && filters.practiceId === "all";

    async function loadData() {
      if (outboundMissingPractice) {
        setOverview(null);
        setSessionsPayload({ rows: [], total: 0 });
        setSessionDetail(null);
        setError("");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const overviewUrl =
          filters.section === "outbound"
            ? "/api/voice-ops/outbound-overview"
            : "/api/voice-ops/overview";
        const overviewRequest = axios.get(overviewUrl, {
          headers,
          params: applySectionDirection(buildApiParams(filters), filters),
          signal: controller.signal,
        });
        const sessionsRequest =
          filters.mode === "dev"
            ? axios.get("/api/voice-ops/sessions", {
                headers,
                params: {
                  ...applySectionDirection(buildApiParams(filters), filters),
                  limit: 60,
                },
                signal: controller.signal,
              })
            : Promise.resolve(null);
        const detailRequest = sessionId
          ? axios.get(`/api/voice-ops/sessions/${sessionId}`, {
              headers,
              signal: controller.signal,
            })
          : Promise.resolve(null);

        const [overviewResponse, sessionsResponse, detailResponse] =
          await Promise.all([overviewRequest, sessionsRequest, detailRequest]);

        if (disposed) return;

        setOverview(overviewResponse.data);
        if (sessionsResponse) {
          setSessionsPayload(sessionsResponse.data);
        } else {
          setSessionsPayload({ rows: [], total: 0 });
        }
        setSessionDetail(detailResponse ? detailResponse.data : null);
      } catch (requestError) {
        if (disposed || axios.isCancel(requestError)) return;
        setError(
          requestError?.response?.data?.error ||
            "Voice Ops data could not be loaded.",
        );
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      disposed = true;
      controller.abort();
    };
  }, [filters, refreshNonce, sessionId, token]);

  useEffect(() => {
    if (!token || !isVoiceOpsEnabled) return undefined;

    let disposed = false;
    const controller = new AbortController();
    const outboundMissingPractice =
      filters.section === "outbound" && filters.practiceId === "all";

    function stopPolling() {
      if (pollingTimerRef.current) {
        window.clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    }

    function startPolling() {
      if (pollingTimerRef.current) return;
      setStreamStatus("polling");
      pollingTimerRef.current = window.setInterval(() => {
        setRefreshNonce((value) => value + 1);
      }, 15000);
    }

    function handleChunk(chunk) {
      const lines = chunk.split("\n");
      let eventName = "message";
      const dataLines = [];

      lines.forEach((line) => {
        if (line.startsWith("event:")) {
          eventName = line.slice(6).trim();
        }
        if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).trim());
        }
      });

      if (dataLines.length === 0) return;

      try {
        const payload = JSON.parse(dataLines.join("\n"));
        if (eventName === "snapshot") {
          setOverview(payload);
        } else {
          scheduleRefresh();
        }
      } catch (streamError) {
        scheduleRefresh();
      }
    }

    async function connectStream() {
      if (outboundMissingPractice) {
        return;
      }
      setStreamStatus("connecting");
      try {
        const params = new URLSearchParams(
          applySectionDirection(buildApiParams(filters), filters),
        );
        const response = await fetch(`/api/voice-ops/live?${params.toString()}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
          },
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`Stream failed with ${response.status}`);
        }

        reconnectAttemptsRef.current = 0;
        stopPolling();
        setStreamStatus("live");

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (!disposed) {
          const { done, value } = await reader.read();
          if (done) {
            throw new Error("stream_closed");
          }
          buffer += decoder.decode(value, { stream: true });
          let boundary = buffer.indexOf("\n\n");
          while (boundary >= 0) {
            const chunk = buffer.slice(0, boundary).trim();
            buffer = buffer.slice(boundary + 2);
            if (chunk) {
              handleChunk(chunk);
            }
            boundary = buffer.indexOf("\n\n");
          }
        }
      } catch (streamError) {
        if (disposed || controller.signal.aborted) return;
        reconnectAttemptsRef.current += 1;
        if (reconnectAttemptsRef.current >= 3) {
          startPolling();
          return;
        }
        setStreamStatus("reconnecting");
        reconnectTimerRef.current = window.setTimeout(() => {
          connectStream();
        }, reconnectAttemptsRef.current * 2000);
      }
    }

    connectStream();

    return () => {
      disposed = true;
      controller.abort();
      stopPolling();
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [filters, token]);

  useEffect(() => {
    let cancelled = false;

    const releaseRecordingUrl = () => {
      setRecordingMediaUrl((currentUrl) => {
        if (currentUrl && currentUrl.startsWith("blob:")) {
          URL.revokeObjectURL(currentUrl);
        }
        return "";
      });
    };

    async function loadRecording() {
      const practiceId = sessionDetail?.session?.practiceId;
      const session = sessionDetail?.session;
      const recording = sessionDetail?.recording;

      releaseRecordingUrl();
      setRecordingError("");

      if (!session || !practiceId) {
        setRecordingLoading(false);
        return;
      }

      if (
        !recording?.available &&
        !recording?.sid &&
        !recording?.url &&
        !session.callSid &&
        !session.rootCallSid
      ) {
        setRecordingLoading(false);
        return;
      }

      setRecordingLoading(true);

      try {
        let resolvedRecordingSid = recording?.sid || null;

        if (!resolvedRecordingSid) {
          const recordingsResponse = await axios.get("/api/getCallRecordings", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: { PracticeId: practiceId },
          });
          const matchedRecording = findRecordingForSession(
            session,
            recording,
            recordingsResponse.data,
          );
          resolvedRecordingSid = matchedRecording?.sid || null;
        }

        if (resolvedRecordingSid) {
          // Issue #18: the endpoint now returns either a presigned S3 URL (recording already
          // uploaded) or the legacy Twilio-proxied bytes as a blob: object URL (not yet
          // uploaded) - fetchRecordingPlaybackUrl resolves either shape into a single string
          // that can be set directly as the <audio> element's src.
          const playbackUrl = await fetchRecordingPlaybackUrl(
            token,
            resolvedRecordingSid,
            practiceId,
          );

          if (cancelled) return;

          setRecordingMediaUrl(playbackUrl);
          setRecordingLoading(false);
          return;
        }

        if (recording?.url) {
          setRecordingMediaUrl(recording.url);
        }
      } catch (requestError) {
        if (!cancelled) {
          setRecordingError("Recording could not be loaded.");
        }
      } finally {
        if (!cancelled) {
          setRecordingLoading(false);
        }
      }
    }

    loadRecording();

    return () => {
      cancelled = true;
      releaseRecordingUrl();
    };
  }, [sessionDetail, token]);

  if (!isVoiceOpsEnabled) {
    return <Navigate to="/" replace />;
  }

  if (userType !== "admin") {
    return <Navigate to="/" replace />;
  }

  const overviewCards = [
    {
      label: "Active Calls",
      value: overview?.kpis?.activeSessions ?? 0,
      tone: "primary",
      helper: "Live calls in progress",
    },
    {
      label: "Sessions",
      value: overview?.kpis?.totalSessions ?? 0,
      tone: "neutral",
      helper: `${filters.range} window`,
    },
    {
      label: "Success Rate",
      value: formatPercent(overview?.kpis?.successRate ?? 0),
      tone: "success",
      helper: "Completed / ended",
    },
    {
      label: "Avg Duration",
      value: formatDuration(overview?.kpis?.avgDurationSec ?? 0),
      tone: "neutral",
      helper: "Across filtered calls",
    },
    {
      label: "Total Tokens",
      value: formatCompact(overview?.kpis?.totalTokens ?? 0),
      tone: "neutral",
      helper: "Prompt + completion",
    },
  ];

  const isOutboundSection = filters.section === "outbound";
  const outboundMissingPractice =
    isOutboundSection && filters.practiceId === "all";

  const outboundQueue = overview?.queue || {};
  const outboundKpis = overview?.kpis || {};
  const outboundTotalSessions = outboundKpis.totalSessions ?? 0;

  const outboundCards = [
    {
      label: "Remaining",
      value: outboundQueue.remaining ?? 0,
      tone: "primary",
      helper: `Not yet dialed, ${filters.range} window`,
    },
    {
      label: "Active Calls",
      value:
        outboundQueue.concurrencyLimit != null
          ? `${outboundKpis.activeSessions ?? 0} / ${outboundQueue.concurrencyLimit}`
          : outboundKpis.activeSessions ?? 0,
      tone: "primary",
      helper:
        outboundQueue.concurrencyLimit != null
          ? "Live / concurrency limit"
          : "Live calls in progress",
    },
    {
      label: "Attempted Today",
      value: outboundTotalSessions,
      tone: "neutral",
      helper: `${filters.range} window`,
    },
    {
      label: "Total List",
      value: outboundQueue.totalListSize ?? 0,
      tone: "neutral",
      helper: "Uploaded in window",
    },
    {
      label: "Avg Duration",
      value: formatDuration(outboundKpis.avgDurationSec ?? 0),
      tone: "neutral",
      helper: "Across filtered calls",
    },
  ];

  const selectedSession = sessionDetail?.session;
  const trendRows = Array.isArray(overview?.trends) ? overview.trends : [];
  const maxTrendCalls = Math.max(
    1,
    ...trendRows.map((trend) => Number(trend.callsStarted || 0)),
  );

  return (
    <div className="voice-ops-page-shell">
      <div className="voice-ops-header">
        <div>
          <p className="voice-ops-eyebrow">Admin Only</p>
          <h1>Voice Ops</h1>
          <p className="voice-ops-subtitle">
            Live call telemetry across LiveKit, Twilio, TTS, and Bedrock.
          </p>
        </div>
        <div className="voice-ops-header-actions">
          <div className="voice-ops-mode-toggle">
            <button
              className={filters.mode === "exec" ? "active" : ""}
              onClick={() => {
                if (sessionId) {
                  navigate(`/VoiceOps?${buildParamsString(filters, "exec")}`);
                  return;
                }
                updateFilters({ mode: "exec" });
              }}
            >
              Executive
            </button>
            <button
              className={filters.mode === "dev" ? "active" : ""}
              onClick={() => updateFilters({ mode: "dev" })}
            >
              Developer
            </button>
          </div>
          <div className="voice-ops-mode-toggle">
            <button
              className={!isOutboundSection ? "active" : ""}
              onClick={() => updateFilters({ section: "all" })}
            >
              All
            </button>
            <button
              className={isOutboundSection ? "active" : ""}
              onClick={() => updateFilters({ section: "outbound" })}
            >
              Outbound
            </button>
          </div>
          {renderStatusPill(
            streamStatus === "live"
              ? "Live"
              : streamStatus === "polling"
                ? "Polling Fallback"
                : "Reconnecting",
            streamStatus === "live"
              ? "healthy"
              : streamStatus === "polling"
                ? "warning"
                : "muted",
          )}
        </div>
      </div>

      <div className="voice-ops-filter-bar">
        <div className="voice-ops-filter-top-row">
          <label>
            Practice
            <select
              value={filters.practiceId}
              onChange={(event) => updateFilters({ practiceId: event.target.value })}
            >
              <option value="all" disabled={isOutboundSection}>
                {isOutboundSection ? "Select a practice..." : "All Practices"}
              </option>
              {adminPractices.map((practice) => (
                <option key={practice.Id} value={practice.Id}>
                  {practice.PracticeName}
                </option>
              ))}
            </select>
          </label>
          <div className="voice-ops-filter-menu-shell" ref={filterMenuRef}>
            <span className="voice-ops-filter-menu-label">Filter By</span>
            <button
              type="button"
              className={`voice-ops-filter-menu-button ${isFilterMenuOpen ? "open" : ""}`}
              onClick={() => setIsFilterMenuOpen((value) => !value)}
            >
              <span>Filter By</span>
              <strong>
                {countAppliedFilters(filters) > 0
                  ? `${countAppliedFilters(filters)} active`
                  : "Default"}
              </strong>
            </button>
            {isFilterMenuOpen ? (
              <div className="voice-ops-filter-popover">
                <label>
                  Range
                  <select
                    value={filters.range}
                    onChange={(event) => updateFilters({ range: event.target.value })}
                  >
                    <option value="15m">15m</option>
                    <option value="1h">1h</option>
                    <option value="3h">3h</option>
                    <option value="8h">8h</option>
                    <option value="24h">24h</option>
                    <option value="7d">7d</option>
                    <option value="30d">30d</option>
                  </select>
                </label>
                <label>
                  Status
                  <select
                    value={filters.status}
                    onChange={(event) => updateFilters({ status: event.target.value })}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </select>
                </label>
                {isOutboundSection ? null : (
                  <label>
                    Direction
                    <select
                      value={filters.direction}
                      onChange={(event) =>
                        updateFilters({ direction: event.target.value })
                      }
                    >
                      <option value="">All</option>
                      <option value="inbound">Inbound</option>
                      <option value="outbound">Outbound</option>
                    </select>
                  </label>
                )}
                <label>
                  Transfer
                  <select
                    value={filters.transferState}
                    onChange={(event) =>
                      updateFilters({ transferState: event.target.value })
                    }
                  >
                    <option value="">All</option>
                    <option value="attempted">Attempted</option>
                    <option value="connected">Connected</option>
                    <option value="none">None</option>
                  </select>
                </label>
              </div>
            ) : null}
          </div>
          <label className="voice-ops-filter-search">
            Search
            <input
              type="text"
              placeholder="callSid, rootCallSid, room, to/from"
              value={filters.search}
              onChange={(event) => updateFilters({ search: event.target.value })}
            />
          </label>
        </div>
        <div className="voice-ops-filter-summary-row">
          <span className="voice-ops-filter-summary-title">Filters Applied</span>
          <span className="voice-ops-filter-menu-summary">
            {getFilterSummary(filters)}
          </span>
        </div>
      </div>

      {error ? <div className="voice-ops-banner error">{error}</div> : null}

      {outboundMissingPractice ? (
        <div className="voice-ops-empty">
          Select a practice above to view outbound metrics.
        </div>
      ) : (
        <>
          <div className="voice-ops-kpis">
            {(isOutboundSection ? outboundCards : overviewCards).map((card) => (
              <div key={card.label} className={`voice-ops-card tone-${card.tone}`}>
                <span className="voice-ops-card-label">{card.label}</span>
                <strong className="voice-ops-card-value">{card.value}</strong>
                <span className="voice-ops-card-helper">{card.helper}</span>
              </div>
            ))}
          </div>

          {loading && !overview ? (
            <div className="voice-ops-empty">Loading Voice Ops telemetry...</div>
          ) : null}
        </>
      )}

      {outboundMissingPractice ? null : filters.mode === "exec" ? (
        <>
          <div className="voice-ops-grid exec-grid">
            <section className="voice-ops-panel">
              <div className="voice-ops-panel-header">
                <h2>Executive Funnel</h2>
                <span>{overview?.bucket || "1h"} buckets</span>
              </div>
              <div className="voice-ops-funnel-scroll">
                <div className="voice-ops-funnel">
                  {[
                    ["Attempted", overview?.funnel?.attempted ?? 0],
                    ["Connected", overview?.funnel?.connected ?? 0],
                    ["Human", overview?.funnel?.humanAnswered ?? 0],
                    ["Voicemail", overview?.funnel?.voicemail ?? 0],
                    ["Transfer", overview?.funnel?.transferAttempted ?? 0],
                    ["Completed", overview?.funnel?.completed ?? 0],
                  ].map(([label, value]) => (
                    <div key={label} className="voice-ops-funnel-step">
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="voice-ops-panel">
              <div className="voice-ops-panel-header">
                <h2>Latency And Volume</h2>
                <span>{trendRows.length} points</span>
              </div>
              <div className="voice-ops-trend">
                {trendRows.length === 0 ? (
                  <div className="voice-ops-empty compact">No trend data yet.</div>
                ) : (
                  trendRows.map((trend) => (
                    <div key={trend.bucketStart} className="voice-ops-trend-row">
                      <span className="voice-ops-trend-label">
                        {formatDateTime(trend.bucketStart, practiceTimezone)}
                      </span>
                      <div className="voice-ops-trend-bar-shell">
                        <div
                          className="voice-ops-trend-bar"
                          style={{
                            width: `${Math.max(
                              8,
                              (Number(trend.callsStarted || 0) / maxTrendCalls) * 100,
                            )}%`,
                          }}
                        />
                      </div>
                        <strong>{trend.callsStarted}</strong>
                        <span className="voice-ops-trend-meta">
                        {trend.averageConversationLatencyMs
                          ? formatLatency(trend.averageConversationLatencyMs)
                          : "No latency"}
                        </span>
                      </div>
                    ))
                )}
              </div>
            </section>
          </div>

          <div className="voice-ops-grid exec-grid">
            <section className="voice-ops-panel">
              <div className="voice-ops-panel-header">
                <h2>Provider Health</h2>
                <span>Live service posture</span>
              </div>
              <div className="voice-ops-provider-grid">
                {providerHealthCards.map((provider) => (
                  <button
                    key={provider.source}
                    type="button"
                    className={`voice-ops-provider-card ${
                      provider.source === "twilio" ? "interactive" : "static"
                    }`}
                    onClick={
                      provider.source === "twilio"
                        ? () => setIsTwilioCodesOpen(true)
                        : undefined
                    }
                    disabled={provider.source !== "twilio"}
                  >
                    <div className="voice-ops-provider-header">
                      <strong>{formatProviderLabel(provider.source)}</strong>
                      {renderStatusPill(
                        provider.status.replace("_", " "),
                        getHealthTone(provider.status),
                      )}
                    </div>
                    <span>
                      {provider.metricLabel === "voice_calls" ? "Voice Calls" : "Events"}:{" "}
                      {provider.totalEvents}
                    </span>
                    <span>
                      {provider.metricLabel === "voice_calls" ? "Important Incidents" : "Errors"}:{" "}
                      {provider.errorEvents}
                    </span>
                    <span>
                      Error Rate:{" "}
                      {formatPercentOrNA(
                        provider.errorRate,
                        provider.errorRateAvailable !== false,
                      )}
                    </span>
                    <span>
                      Avg Latency:{" "}
                      {formatLatency(provider.avgConversationLatencyMs)}
                    </span>
                    <span>Last Seen: {formatDateTime(provider.lastSeenAt, practiceTimezone)}</span>
                    {provider.source === "twilio" ? (
                      <span className="voice-ops-provider-note">
                        Click to inspect Twilio alert codes
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </section>

            <section className="voice-ops-panel">
              <div className="voice-ops-panel-header">
                <h2>Recent Sessions</h2>
                <span>Click through to developer drill-down</span>
              </div>
              <div className="voice-ops-table-shell voice-ops-table-shell--sessions">
                <table className="voice-ops-table">
                  <thead>
                    <tr>
                      <th>Started</th>
                      <th>Practice</th>
                      <th>Status</th>
                      <th>Direction</th>
                      <th>Latency</th>
                      <th>Release</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(overview?.recentSessions || []).map((row) => (
                      <tr key={row.sessionId} onClick={() => openSession(row.sessionId)}>
                        <td>{formatDateTime(row.startedAt, practiceTimezone)}</td>
                        <td>{row.practiceId || "N/A"}</td>
                        <td>{row.status}</td>
                        <td>{row.direction || "Unknown"}</td>
                        <td>
                          {formatLatency(row.lastConversationLatencyMs)}
                        </td>
                        <td>
                          {renderReleaseBadge(row.releaseDigest, row.configSource, row.workerBuildSha)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </>
      ) : (
        <div className="voice-ops-grid dev-grid">
          <section className="voice-ops-panel voice-ops-sessions-panel">
            <div className="voice-ops-panel-header">
              <h2>Sessions</h2>
              <span>{sessionsPayload.total} matching rows</span>
            </div>
            <div className="voice-ops-table-shell voice-ops-table-shell--sessions voice-ops-table-shell--developer">
              <table className="voice-ops-table compact">
                <thead>
                  <tr>
                    <th>Session</th>
                    <th>Status</th>
                    <th>Direction</th>
                    <th>Started</th>
                    <th>Latency</th>
                    <th>Errors</th>
                    <th>Release</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionsPayload.rows.map((row) => (
                    <tr
                      key={row.sessionId}
                      className={sessionId === row.sessionId ? "selected" : ""}
                      onClick={() => openSession(row.sessionId)}
                    >
                      <td>
                        <strong className="voice-ops-session-name" title={row.roomName}>
                          {row.roomName}
                        </strong>
                        <span className="voice-ops-inline-meta voice-ops-session-meta">
                          {row.callSid || row.rootCallSid || row.sessionId}
                        </span>
                      </td>
                      <td>{row.status}</td>
                      <td>{row.direction || "Unknown"}</td>
                      <td>{formatDateTime(row.startedAt, practiceTimezone)}</td>
                      <td>{formatLatency(row.lastConversationLatencyMs)}</td>
                      <td>{row.toolErrorCount}</td>
                      <td>
                        {renderReleaseBadge(row.releaseDigest, row.configSource, row.workerBuildSha)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="voice-ops-panel voice-ops-detail-panel">
            <div className="voice-ops-panel-header">
              <h2>Session Detail</h2>
              {sessionId ? (
                <button className="voice-ops-link-button" onClick={closeSession}>
                  Close Detail
                </button>
              ) : (
                <span>Select a session</span>
              )}
            </div>

            {!sessionId ? (
              <div className="voice-ops-empty">
                Select a session to inspect transcript, provider events, tools, and
                live latency metrics.
              </div>
            ) : !sessionDetail ? (
              <div className="voice-ops-empty">Loading session detail...</div>
            ) : (
              <>
                <div className="voice-ops-detail-header">
                  <div className="voice-ops-detail-copy">
                    <h3 className="voice-ops-detail-title" title={selectedSession?.roomName}>
                      {selectedSession?.roomName}
                    </h3>
                    <span className="voice-ops-detail-id">{selectedSession?.sessionId}</span>
                  </div>
                  <div className="voice-ops-detail-actions">
                    <div className="voice-ops-detail-badges">
                      {renderStatusPill(selectedSession?.status || "unknown", "muted")}
                      {renderStatusPill(
                        selectedSession?.active ? "Active" : "Ended",
                        selectedSession?.active ? "healthy" : "muted",
                      )}
                      {renderReleaseBadge(
                        selectedSession?.releaseDigest,
                        selectedSession?.configSource,
                        selectedSession?.workerBuildSha,
                      )}
                    </div>
                    <button
                      type="button"
                      className="voice-ops-secondary-button"
                      onClick={() => setIsMoreDetailsOpen(true)}
                    >
                      More Details
                    </button>
                  </div>
                </div>

                <div className="voice-ops-detail-grid">
                  <div className="voice-ops-card">
                    <span className="voice-ops-card-label">Summary</span>
                    <p className="voice-ops-summary-text">
                      {sessionDetail.summary || "No summary available yet."}
                    </p>
                  </div>
                  <div className="voice-ops-card">
                    <span className="voice-ops-card-label">Recording</span>
                    {recordingLoading ? (
                      <p className="voice-ops-summary-text">Loading recording...</p>
                    ) : recordingMediaUrl ? (
                      <div className="voice-ops-recording-shell">
                        <audio
                          controls
                          preload="none"
                          className="voice-ops-recording-player"
                          src={recordingMediaUrl}
                        />
                        <a
                          href={recordingMediaUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="voice-ops-recording-link"
                        >
                          Open recording
                        </a>
                      </div>
                    ) : (
                      <p className="voice-ops-summary-text">
                        {recordingError || "Recording unavailable or not configured."}
                      </p>
                    )}
                    <div className="voice-ops-meta-list">
                      <span>Started: {formatDateTime(selectedSession?.startedAt, practiceTimezone)}</span>
                      <span>Ended: {formatDateTime(selectedSession?.endedAt, practiceTimezone)}</span>
                      <span>
                        Latency: {formatLatency(selectedSession?.lastConversationLatencyMs)}
                      </span>
                      <span>CallSid: {selectedSession?.callSid || "Unavailable"}</span>
                      <span>
                        RootCallSid: {selectedSession?.rootCallSid || "Unavailable"}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {isTwilioCodesOpen ? (
        <div
          className="voice-ops-modal-backdrop"
          onClick={() => setIsTwilioCodesOpen(false)}
        >
          <div
            className="voice-ops-modal voice-ops-modal--narrow"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="voice-ops-modal-header">
              <div>
                <h2>Twilio Alert Codes</h2>
                <span>
                  {(overview?.twilioHealth?.includedCodes || []).join(", ")} drive the
                  Twilio health %
                </span>
              </div>
              <button
                type="button"
                className="voice-ops-secondary-button"
                onClick={() => setIsTwilioCodesOpen(false)}
              >
                Close
              </button>
            </div>

            <section className="voice-ops-subpanel">
              <div className="voice-ops-subpanel-header">
                <h3>Observed Codes</h3>
                <span>{twilioObservedCodes.length} codes</span>
              </div>
              <div className="voice-ops-table-shell voice-ops-table-shell--modal">
                {twilioObservedCodes.length === 0 ? (
                  <div className="voice-ops-empty compact">
                    No Twilio alerts in the selected time range.
                  </div>
                ) : (
                  <table className="voice-ops-table compact">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Count</th>
                        <th>Health %</th>
                        <th>Last Seen</th>
                        <th>Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      {twilioObservedCodes.map((row) => (
                        <tr key={row.code}>
                          <td>{row.code}</td>
                          <td>{row.count}</td>
                          <td>{row.includedInRate ? "Included" : "List only"}</td>
                          <td>{formatDateTime(row.lastSeenAt, practiceTimezone)}</td>
                          <td>{row.sampleMessage || "No decoded message"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </div>
        </div>
      ) : null}

      {isMoreDetailsOpen && sessionDetail ? (
        <div
          className="voice-ops-modal-backdrop"
          onClick={() => setIsMoreDetailsOpen(false)}
        >
          <div className="voice-ops-modal" onClick={(event) => event.stopPropagation()}>
            <div className="voice-ops-modal-header">
              <div>
                <h2>Session Details</h2>
                <span>{selectedSession?.roomName}</span>
              </div>
              <button
                type="button"
                className="voice-ops-secondary-button"
                onClick={() => setIsMoreDetailsOpen(false)}
              >
                Close
              </button>
            </div>

            <div className="voice-ops-modal-grid">
              <section className="voice-ops-subpanel">
                <div className="voice-ops-subpanel-header">
                  <h3>Turn Metrics</h3>
                  <span>{sessionDetail.turnMetrics?.length || 0} turns</span>
                </div>
                <div className="voice-ops-table-shell">
                  <table className="voice-ops-table compact">
                    <thead>
                      <tr>
                        <th>Turn</th>
                        <th>EOU</th>
                        <th>STT</th>
                        <th>LLM</th>
                        <th>TTS</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sessionDetail.turnMetrics || []).map((turn) => (
                        <tr key={turn.speechId}>
                          <td>{turn.speechId}</td>
                          <td>{formatLatency(turn.eouDelayMs).replace("Unavailable", "n/a")}</td>
                          <td>{formatLatency(turn.transcriptionDelayMs).replace("Unavailable", "n/a")}</td>
                          <td>{formatLatency(turn.llmTtftMs).replace("Unavailable", "n/a")}</td>
                          <td>{formatLatency(turn.ttsTtfbMs).replace("Unavailable", "n/a")}</td>
                          <td>
                            {formatLatency(turn.conversationLatencyMs).replace(
                              "Unavailable",
                              "n/a",
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="voice-ops-subpanel">
                <div className="voice-ops-subpanel-header">
                  <h3>Tools</h3>
                  <span>{sessionDetail.toolCalls?.length || 0} calls</span>
                </div>
                <div className="voice-ops-table-shell">
                  <table className="voice-ops-table compact">
                    <thead>
                      <tr>
                        <th>Tool</th>
                        <th>Status</th>
                        <th>Latency</th>
                        <th>Started</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sessionDetail.toolCalls || []).map((tool) => (
                        <tr key={`${tool.toolName}-${tool.requestId || tool.startedAt}`}>
                          <td>{tool.toolName}</td>
                          <td>{tool.status}</td>
                          <td>{formatLatency(tool.latencyMs).replace("Unavailable", "n/a")}</td>
                          <td>{formatDateTime(tool.startedAt || tool.finishedAt, practiceTimezone)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="voice-ops-subpanel">
                <div className="voice-ops-subpanel-header">
                  <h3>Transcript</h3>
                  <span>{sessionDetail.transcript?.length || 0} items</span>
                </div>
                <div className="voice-ops-transcript">
                  {(sessionDetail.transcript || []).map((entry, index) => (
                    <div
                      key={`${entry.occurredAt}-${index}`}
                      className={`voice-ops-transcript-line ${entry.role}`}
                    >
                      <strong>{entry.role}</strong>
                      <span>{entry.content || "(empty)"}</span>
                      <time>{formatDateTime(entry.occurredAt, practiceTimezone)}</time>
                    </div>
                  ))}
                </div>
              </section>

            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

